import { Component } from '@theme/component';
import { closest, clamp, center, getVisibleElements } from '@theme/utilities';
import { SlideshowSelectEvent } from '@theme/events';
import { Scroller } from '@theme/scrolling';
import { cycleFocus } from '@theme/focus';

/**
 * Collection links component
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} container
 * @property {HTMLElement[]} [images]
 * @property {HTMLElement[]} [links]
 * @property {import('slideshow').Slideshow} slideshow
 *
 * @extends {Component<Refs>}
 */
class CollectionLinks extends Component {
  requiredRefs = ['container'];

  /** @type {Scroller} */
  #scroll;

  /** @type {ResizeObserver} */
  #hoverImageBoundsObserver;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keydown', this.#handleKeydown);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);

    this.#scroll = new Scroller(this.refs.container, { onScroll: this.#handleScroll });

    // Text layout only: the hover-image frame (blocks/_collection-link.liquid)
    // is a fixed size centered on `container`, but `container` is only as
    // tall as the wrapped category text, which varies with viewport width
    // and collection count. Measuring the actual space between container's
    // center and this section's own box - recomputed on any resize, since
    // wrapping can change - lets the frame's CSS (max-height: var(...))
    // shrink itself to whatever's genuinely available, so it can never rely
    // on the section's overflow: hidden to hide a cropped image.
    const section = this.refs.container.closest('.section');
    if (section) {
      this.#hoverImageBoundsObserver = new ResizeObserver(this.#updateHoverImageBounds);
      this.#hoverImageBoundsObserver.observe(this.refs.container);
      this.#hoverImageBoundsObserver.observe(section);
      this.#updateHoverImageBounds();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#scroll.destroy();
    this.#hoverImageBoundsObserver?.disconnect();
  }

  #updateHoverImageBounds = () => {
    const { container } = this.refs;
    const section = container.closest('.section');
    if (!section) return;

    const containerRect = container.getBoundingClientRect();
    const sectionRect = section.getBoundingClientRect();
    const centerY = containerRect.top + containerRect.height / 2;
    const availableAbove = (centerY - sectionRect.top) * 2;
    const availableBelow = (sectionRect.bottom - centerY) * 2;
    const maxHeight = Math.max(0, Math.min(availableAbove, availableBelow));

    container.style.setProperty('--hover-image-max-block-size', `${maxHeight}px`);
  };

  get links() {
    return this.refs.links || [];
  }

  get currentIndex() {
    return this.links.findIndex((link) => link.getAttribute('aria-current') === 'true');
  }

  /**
   * Public method to select a collection link. Called two ways:
   * - internally, with a real numeric index and no event (e.g. #handleScroll)
   * - from the markup's on:pointerenter="/select" / on:focus="/select" (no
   *   path segment - the category blocks are independently addable now, so
   *   there's no reliable forloop index to bake into that action string),
   *   which the `on:` framework invokes as select(event) - a single
   *   argument. Detecting that case and resolving the index from the
   *   event's own target keeps this correct regardless of how many blocks
   *   exist or what order they render in.
   *
   * @param {number|PointerEvent} targetIndex
   * @param {PointerEvent} [event]
   */
  select(targetIndex, event) {
    if (targetIndex instanceof Event) {
      event = targetIndex;
      targetIndex = this.links.indexOf(/** @type {HTMLElement} */ (event.target));
    }

    this.#updateSelectedLink(targetIndex);
    this.refs.slideshow?.select(targetIndex, undefined, { animate: false });
    if (event) this.#revealImage(event);
  }

  /**
   * Update the selected link
   *
   * @param {number} index
   */
  #updateSelectedLink(index) {
    const { links } = this;
    const selectedIndex = clamp(index, 0, links.length - 1);

    for (const [index, link] of links.entries()) {
      link.setAttribute('aria-current', Boolean(index === selectedIndex).toString());
    }
  }

  /**
   * Handle slideshow select event
   *
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = async (event) => {
    if (!event.detail.userInitiated) return;

    const { index } = event.detail;
    if (index === this.currentIndex) return;

    const selectedLink = this.links[index];
    if (!selectedLink) return;

    this.#updateSelectedLink(index);

    this.#scroll.to(selectedLink);
  };

  /**
   * Cycle focus to the next or previous link
   *
   * @param {KeyboardEvent} event
   */
  #handleKeydown(event) {
    let modifier = 0;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        modifier = 1;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        modifier = -1;
        break;
    }

    if (!modifier) return;

    event.preventDefault();
    cycleFocus(this.links, modifier);
  }

  /**
   * Handle scroll event
   */
  #handleScroll = () => {
    const { links } = this;
    const { container } = this.refs;
    const visibleLinks = getVisibleElements(this, links, 0.1);

    if (visibleLinks.length === 0) return;
    const centers = visibleLinks.map((link) => center(link, 'x'));
    const referencePoint = center(container, 'x');
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleLink = visibleLinks[centers.indexOf(closestCenter)];

    if (!closestVisibleLink) return;

    const index = links.indexOf(closestVisibleLink);

    this.select(index);
  };

  /**
   * Clear all selections when the pointer leaves the container - or, when
   * data-default-index is set (homepage category menu only -
   * sections/collection-links.liquid, template.name == 'index'), restore
   * that link/image instead of clearing to blank, so the menu falls back
   * to its default-active category rather than an empty state. Absent
   * elsewhere (e.g. the collection page's single-line menu), which keeps
   * its original clear-to-blank behavior exactly as before.
   */
  clearSelections = () => {
    const { links } = this;
    const { images } = this.refs;
    const { defaultIndex } = this.dataset;

    if (defaultIndex !== undefined) {
      this.#updateSelectedLink(Number(defaultIndex));

      if (images) {
        const defaultHoverImageId = links[Number(defaultIndex)]?.dataset.hoverImageId;
        for (const image of images) {
          image.toggleAttribute('reveal', image.dataset.hoverImageId === defaultHoverImageId);
        }
      }

      return;
    }

    // Reset all links to unselected state (opacity will reset via CSS)
    for (const link of links) {
      link.setAttribute('aria-current', 'false');
    }

    // Hide any revealed images
    if (images) {
      for (const image of images) {
        image.removeAttribute('reveal');
      }
    }
  };

  /**
   * Reveal the hovered/focused link's background image and hide the rest.
   * The image is positioned entirely by CSS (an absolutely-positioned layer
   * behind the text, sized against the container) — this just toggles which
   * one is visible, no cursor-position tracking needed.
   *
   * Matched via data-hover-image-id (the block's own Shopify block.id, set
   * on both the link and its image in blocks/_collection-link.liquid)
   * rather than by array position: the Theme Editor wraps each rendered
   * block in its own element for selection/highlighting, which can change
   * how `links[]` and `images[]` line up positionally even though both
   * arrays are collected via the same ref system — indexOf(target) into
   * one array and reading the other by that index silently breaks the
   * moment the two arrays aren't in identical order. Matching by the
   * block's own id is immune to that: it doesn't matter where either
   * element ends up in the DOM, only that they share the same id.
   *
   * @param {Event} event
   */
  #revealImage(event) {
    if (!(event instanceof PointerEvent)) return;
    if (event.pointerType === 'touch') return;

    const { target } = event;
    if (!(target instanceof HTMLElement)) return;

    const { images } = this.refs;
    if (!images) return;

    const hoverImageId = target.dataset.hoverImageId;
    const selectedImage = images.find((image) => image.dataset.hoverImageId === hoverImageId);

    for (const image of images) {
      image.toggleAttribute('reveal', image === selectedImage);
    }
  }
}

if (!customElements.get('collection-links-component')) {
  customElements.define('collection-links-component', CollectionLinks);
}
